import { readFile, writeFile } from "node:fs/promises";
import path from "node:path";

import { ensureDir } from "../lib/fs.js";
import { buildImportGraph } from "../lib/import-graph.js";
import { buildUnifiedDiff } from "../lib/text-diff.js";
import {
  createCopyId,
  ensureSafeTargetPath,
  fileExists,
  isBinaryBuffer,
  parseManifest,
  readJsonFile,
  resolveDependencyRequirements,
  resolveManifestCandidateFiles,
  resolveSingleExportDirectory,
  toProjectRelative,
  toPosixPath,
} from "./copy-service-shared.js";
import type {
  PrimeUiCopyExportableConflictReport,
  PrimeUiCopyExportableResult,
  PrimeUiConflictFile,
  PrimeUiCopyConflictReportEntry,
  PrimeUiSkippedCopyFile,
} from "../types.js";

type CopyExportableServiceInput = {
  projectRoot: string;
  exportsRoot: string;
  exportableKey: string;
};

const PACKAGE_JSON_RELATIVE_PATH = "package.json";
const SUPPLEMENTARY_DEPENDENCY_PACKAGES: Readonly<Record<string, string[]>> = {
  cookieBanner: ["@types/js-cookie"],
};

function normalizeExportableKey(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error("exportableKey must not be empty.");
  }

  return trimmed;
}

function createNotAvailableResult(input: {
  exportId: string;
  exportPath: string;
  exportableKey: string;
  isReadyToExport: boolean;
  manifestSuccess: boolean;
  manifestMessage: string;
  manifestFiles: string[];
  skippedFiles?: PrimeUiSkippedCopyFile[];
  availabilityHint?: string;
}): PrimeUiCopyExportableResult {
  const skippedFiles = input.skippedFiles ?? [];
  const availabilityHint =
    input.availabilityHint ??
    (input.isReadyToExport
      ? "Open the PrimeUI project and regenerate the export after the shared exportable produces files successfully."
      : "Enable or configure this shared exportable in PrimeUI project settings first, then create and download a fresh export.");

  return {
    status: "not_available",
    message: `Shared exportable "${input.exportableKey}" is not available for copy. ${availabilityHint} Details: ${input.manifestMessage}`,
    reportPath: null,
    exportId: input.exportId,
    exportPath: input.exportPath,
    exportableKey: input.exportableKey,
    isReadyToExport: input.isReadyToExport,
    manifestSuccess: input.manifestSuccess,
    manifestMessage: input.manifestMessage,
    manifestFiles: input.manifestFiles,
    newFiles: [],
    identicalFiles: [],
    conflictFiles: [],
    skippedFiles,
    missedDependencies: [],
    dependenciesVersionConflicts: [],
    summary: {
      totalManifestFiles: input.manifestFiles.length,
      totalCandidateFiles: 0,
      copiedFiles: 0,
      identicalFiles: 0,
      conflictFiles: 0,
      skippedFiles: skippedFiles.length,
      missedDependencies: 0,
      dependenciesVersionConflicts: 0,
    },
  };
}

function resolveSupplementaryDependencyPackages(
  exportableKey: string,
): string[] {
  return SUPPLEMENTARY_DEPENDENCY_PACKAGES[exportableKey] ?? [];
}

export async function copyExportableFromExport(
  input: CopyExportableServiceInput,
): Promise<PrimeUiCopyExportableResult> {
  const normalizedExportableKey = normalizeExportableKey(input.exportableKey);

  const { exportId, exportPath } = await resolveSingleExportDirectory(
    input.exportsRoot,
  );
  const manifestPath = path.join(
    input.exportsRoot,
    `${exportId}.manifest.json`,
  );

  if (!(await fileExists(manifestPath))) {
    throw new Error(
      `Export manifest is missing at ${manifestPath}. Run create_export and download_export to regenerate it.`,
    );
  }

  const manifest = parseManifest(await readJsonFile<unknown>(manifestPath));
  if (manifest.export.id !== exportId) {
    throw new Error(
      `Manifest export id mismatch: expected ${exportId}, got ${manifest.export.id}. Re-run create_export and download_export.`,
    );
  }

  const exportable = manifest.export.exportables.find(
    (item) => item.key === normalizedExportableKey,
  );
  if (!exportable) {
    throw new Error(
      `Shared exportable not found in manifest for key: ${normalizedExportableKey}`,
    );
  }

  const manifestFiles = exportable.manifest.files.map((filePath) =>
    toPosixPath(filePath).replace(/^\.\/+/, ""),
  );

  if (
    !exportable.isReadyToExport ||
    !exportable.manifest.success ||
    manifestFiles.length === 0
  ) {
    return createNotAvailableResult({
      exportId,
      exportPath,
      exportableKey: normalizedExportableKey,
      isReadyToExport: exportable.isReadyToExport,
      manifestSuccess: exportable.manifest.success,
      manifestMessage: exportable.manifest.message,
      manifestFiles,
    });
  }

  const skippedFiles: PrimeUiSkippedCopyFile[] = [];
  const copyManagedManifestFiles = manifestFiles.filter((relativePath) => {
    if (relativePath !== PACKAGE_JSON_RELATIVE_PATH) {
      return true;
    }

    skippedFiles.push({
      targetPath: relativePath,
      reason: "package_json_managed_as_dependencies",
    });
    return false;
  });

  if (copyManagedManifestFiles.length === 0) {
    return createNotAvailableResult({
      exportId,
      exportPath,
      exportableKey: normalizedExportableKey,
      isReadyToExport: exportable.isReadyToExport,
      manifestSuccess: exportable.manifest.success,
      manifestMessage: exportable.manifest.message,
      manifestFiles,
      skippedFiles,
      availabilityHint:
        "No copyable files remain after filtering non-copyable manifest entries such as package.json. Adjust the PrimeUI exportable configuration and create a fresh export before retrying.",
    });
  }

  const candidateFiles = await resolveManifestCandidateFiles({
    exportPath,
    manifestOwnerLabel: `exportable "${normalizedExportableKey}"`,
    files: copyManagedManifestFiles,
  });
  const dependencyGraph = await buildImportGraph({
    projectRoot: exportPath,
    entryFiles: candidateFiles,
    followInternalImports: false,
  });

  const newFiles: string[] = [];
  const identicalFiles: string[] = [];
  const conflictFiles: PrimeUiConflictFile[] = [];
  const conflictReportEntries: PrimeUiCopyConflictReportEntry[] = [];

  for (const sourceFilePath of candidateFiles) {
    const relativeToExport = path.relative(exportPath, sourceFilePath);
    if (
      relativeToExport.startsWith("..") ||
      path.isAbsolute(relativeToExport)
    ) {
      throw new Error(`Source file is outside export root: ${sourceFilePath}`);
    }

    const targetFilePath = path.join(input.projectRoot, relativeToExport);
    ensureSafeTargetPath(input.projectRoot, targetFilePath);

    const sourceBuffer = await readFile(sourceFilePath);
    let targetBuffer: Buffer | null = null;
    try {
      targetBuffer = await readFile(targetFilePath);
    } catch {
      targetBuffer = null;
    }

    const sourceRelative = toProjectRelative(exportPath, sourceFilePath);
    const targetRelative = toProjectRelative(input.projectRoot, targetFilePath);

    if (!targetBuffer) {
      await ensureDir(path.dirname(targetFilePath));
      await writeFile(targetFilePath, sourceBuffer);
      newFiles.push(targetRelative);
      continue;
    }

    if (sourceBuffer.equals(targetBuffer)) {
      identicalFiles.push(targetRelative);
      continue;
    }

    const isBinary =
      isBinaryBuffer(sourceBuffer) || isBinaryBuffer(targetBuffer);
    const diff = isBinary
      ? "Binary files differ."
      : buildUnifiedDiff({
          oldText: targetBuffer.toString("utf-8"),
          newText: sourceBuffer.toString("utf-8"),
          oldLabel: `user/${targetRelative}`,
          newLabel: `export/${sourceRelative}`,
        });

    conflictReportEntries.push({
      sourcePath: sourceRelative,
      targetPath: targetRelative,
      diff,
      isBinary,
    });
  }

  const exportPackageJsonPath = path.join(
    exportPath,
    PACKAGE_JSON_RELATIVE_PATH,
  );
  const userPackageJsonPath = path.join(
    input.projectRoot,
    PACKAGE_JSON_RELATIVE_PATH,
  );
  if (!(await fileExists(exportPackageJsonPath))) {
    throw new Error(`Export package.json not found: ${exportPackageJsonPath}`);
  }
  if (!(await fileExists(userPackageJsonPath))) {
    throw new Error(`User package.json not found: ${userPackageJsonPath}`);
  }

  const requiredPackageNames = [
    ...dependencyGraph.externalPackages,
    ...resolveSupplementaryDependencyPackages(normalizedExportableKey),
  ];
  const { missedDependencies, dependenciesVersionConflicts } =
    await resolveDependencyRequirements({
      exportPackageJsonPath,
      userPackageJsonPath,
      requiredPackageNames,
    });

  const sortedConflictReportEntries = conflictReportEntries.sort((a, b) =>
    a.targetPath.localeCompare(b.targetPath),
  );
  for (const entry of sortedConflictReportEntries) {
    conflictFiles.push({
      targetPath: entry.targetPath,
      isBinary: entry.isBinary,
    });
  }

  const copyId = createCopyId();
  const reportPath = path.join(
    input.exportsRoot,
    `${exportId}.copy-exportable-report-${copyId}.json`,
  );
  const report: PrimeUiCopyExportableConflictReport = {
    reportPath,
    generatedAt: new Date().toISOString(),
    exportId,
    exportableKey: normalizedExportableKey,
    conflicts: sortedConflictReportEntries,
  };
  await writeFile(reportPath, `${JSON.stringify(report, null, 2)}\n`, "utf-8");

  const hasFileConflicts = conflictFiles.length > 0;
  const hasDependencyVersionConflicts = dependenciesVersionConflicts.length > 0;
  const needsReview = hasFileConflicts || hasDependencyVersionConflicts;
  const status: PrimeUiCopyExportableResult["status"] = needsReview
    ? "needs_review"
    : "completed";

  const messageParts: string[] = [];
  if (hasFileConflicts) {
    messageParts.push(
      `Conflicts detected while copying shared exportable "${normalizedExportableKey}". Manual resolution is required before integration is complete. Review ${reportPath} for full diffs and resolve conflicts in the context of the target project behavior.`,
    );
  } else if (hasDependencyVersionConflicts) {
    messageParts.push(
      `Dependency version conflicts detected for shared exportable "${normalizedExportableKey}". Manual resolution is required before integration is complete. Review ${reportPath} and dependenciesVersionConflicts in this response.`,
    );
  } else {
    messageParts.push(
      `Shared exportable "${normalizedExportableKey}" copied with no file conflicts or dependency version conflicts. Review ${reportPath} for full local details if needed.`,
    );
  }

  if (missedDependencies.length > 0) {
    messageParts.push(
      "missedDependencies lists required packages that are a substantial and inseparable part of this exportable's functionality. They were not added or installed automatically and must be handled explicitly by the agent or user.",
    );
  }

  if (skippedFiles.length > 0) {
    messageParts.push(
      "package.json is never copied automatically for shared exportables. Dependency follow-up must come from missedDependencies and dependenciesVersionConflicts.",
    );
  }

  return {
    status,
    message: messageParts.join(" "),
    reportPath,
    exportId,
    exportPath,
    exportableKey: normalizedExportableKey,
    isReadyToExport: exportable.isReadyToExport,
    manifestSuccess: exportable.manifest.success,
    manifestMessage: exportable.manifest.message,
    manifestFiles,
    newFiles,
    identicalFiles,
    conflictFiles,
    skippedFiles,
    missedDependencies,
    dependenciesVersionConflicts,
    summary: {
      totalManifestFiles: manifestFiles.length,
      totalCandidateFiles: candidateFiles.length,
      copiedFiles: newFiles.length,
      identicalFiles: identicalFiles.length,
      conflictFiles: conflictFiles.length,
      skippedFiles: skippedFiles.length,
      missedDependencies: missedDependencies.length,
      dependenciesVersionConflicts: dependenciesVersionConflicts.length,
    },
  };
}
